/**
 * All events related to the handling of advertisement.
 *
 * @remarks
 * These are the types of events, which can be subscribed to with `player.addEventListener()`.
 *
 * In general the flow of advertising events is:
 * - AdSlotStarted
 * - n-times AdStarted and AdStopped
 * - AdSlotCompleted (if successful)
 */
export const AdvertisingEvent = {
  /**
   * Ad clip started and is playing.
   *
   * @remarks
   * This will be emitted for every ad clip.
   * The type of the event is AdClipEvent.
   */
  AdStarted: 'advertising:AdStarted',

  /**
   * Ad clip stopped playing.
   *
   * @remarks
   * This will be emitted for every ad clip.
   * The type of the event is AdClipEvent.
   */
  AdStopped: 'advertising:AdStopped',

  /**
   * Ad slot started and is playing. An ad slot consists of multiple ad clips.
   *
   * @remarks
   * Every ad clip will additionally trigger an AdStarted event
   * This will be emitted for every ad slot.
   * The type of the event is AdSlotEvent.
   *
   * You should listen to this event to start your advertising UI and switch from your default content UI.
   */
  AdSlotStarted: 'advertising:AdSlotStarted',

  /**
   * Ad slot stopped playing.
   *
   * @deprecated Please use {@link AdvertisingEvent.AdSlotCompleted}
   *
   * You should not use this event to determine if the ad slot is finished. Use AdSlotCompleted instead.
   * When this event is fired, the state of the player is undetermined. Some calls will return
   * the metadata of the last ad, some already of the content. This also differs depending of the
   * actual player used.
   *
   * @remarks
   * The type of the event is AdSlotEvent.
   */
  AdSlotStopped: 'advertising:AdSlotStopped',

  /**
   * Ad slot completed playing successfully.
   *
   * @remarks
   * This will always be emitted regardless if the ad slot completed successfully or not.
   * This will be emitted for every ad slot.
   * The type of the event is AdSlotEvent.
   *
   * You should listen to this event to tear down your advertising UI and switch back to your default content UI.
   */
  AdSlotCompleted: 'advertising:AdSlotCompleted',
} as const;

/**
 * {@inheritDoc AdvertisingEvent:var}
 */
export type AdvertisingEvent = (typeof AdvertisingEvent)[keyof typeof AdvertisingEvent];
